import { useState, useEffect } from 'react';
import type { BreedingPair } from '../api/breeding';
import { formatDateForInput } from '../utils/formatters';
import CageSelect from './CageSelect';
import {
  BrandModal,
  BrandModalBody,
  BrandModalFooter,
  BrandModalHeader,
} from './ui/BrandModal';

interface EditBreedingPairModalProps {
  isOpen: boolean;
  onClose: () => void;
  onEdit: (id: string, data: Partial<BreedingPair>) => Promise<void>;
  breedingPair: BreedingPair;
}

interface FormData {
  pairingBeginDate: string;
  cageNumber: string;
  nestNumber?: string;
  comments?: string;
  allowHybridization: boolean;
  isActive: boolean;
}

export default function EditBreedingPairModal({ isOpen, onClose, onEdit, breedingPair }: EditBreedingPairModalProps) {
  const [formData, setFormData] = useState<FormData>({
    pairingBeginDate: formatDateForInput(breedingPair.pairingBeginDate),
    cageNumber: breedingPair.cageNumber || '',
    nestNumber: breedingPair.nestNumber || '',
    comments: breedingPair.comments || '',
    allowHybridization: breedingPair.allowHybridization || false,
    isActive: !breedingPair.pairingEndDate,
  });

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const updateData: Partial<BreedingPair> = {
        pairingBeginDate: formData.pairingBeginDate ? new Date(formData.pairingBeginDate).toISOString() : undefined,
        cageNumber: formData.cageNumber,
        nestNumber: formData.nestNumber,
        comments: formData.comments,
        allowHybridization: formData.allowHybridization,
        pairingEndDate: formData.isActive ? undefined : new Date().toISOString()
      };
      await onEdit(breedingPair.id, updateData);
      onClose();
    } catch (error) {
      console.error('Error updating breeding pair:', error);
    }
  };

  useEffect(() => {
    if (breedingPair) {
      setFormData({
        pairingBeginDate: formatDateForInput(breedingPair.pairingBeginDate),
        cageNumber: breedingPair.cageNumber || '',
        nestNumber: breedingPair.nestNumber || '',
        comments: breedingPair.comments || '',
        allowHybridization: breedingPair.allowHybridization || false,
        isActive: !breedingPair.pairingEndDate,
      });
    }
  }, [breedingPair]);

  return (
    <BrandModal open={isOpen} onClose={onClose} maxWidthClass="max-w-lg">
      <BrandModalHeader title="Edit Breeding Pair" onClose={onClose} />
      <form onSubmit={handleSubmit}>
        <BrandModalBody>
          <div className="space-y-4">
            <div>
              <label htmlFor="pairingBeginDate" className="block text-sm font-medium text-slate-700">
                Pairing Date
              </label>
              <input
                type="date"
                name="pairingBeginDate"
                id="pairingBeginDate"
                value={formData.pairingBeginDate}
                onChange={handleChange}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-cyan-500 sm:text-sm"
              />
            </div>
            <div>
              <label htmlFor="cageNumber" className="block text-sm font-medium text-slate-700">
                Cage Number
              </label>
              <CageSelect
                id="cageNumber"
                name="cageNumber"
                value={formData.cageNumber}
                onChange={(cageNumber) =>
                  handleChange({ target: { name: 'cageNumber', value: cageNumber } } as React.ChangeEvent<HTMLInputElement>)
                }
                placeholder="Search or add a cage number…"
              />
            </div>
            <div>
              <label htmlFor="nestNumber" className="block text-sm font-medium text-slate-700">
                Nest Number
              </label>
              <input
                type="text"
                name="nestNumber"
                id="nestNumber"
                value={formData.nestNumber}
                onChange={handleChange}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-cyan-500 sm:text-sm"
              />
            </div>
            <div>
              <label htmlFor="comments" className="block text-sm font-medium text-slate-700">
                Comments
              </label>
              <input
                type="text"
                name="comments"
                id="comments"
                value={formData.comments}
                onChange={handleChange}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-cyan-500 sm:text-sm"
              />
            </div>
            <div className="flex items-center">
              <input
                type="checkbox"
                name="allowHybridization"
                id="allowHybridization"
                checked={formData.allowHybridization}
                onChange={handleChange}
                className="h-4 w-4 text-primary-600 focus:ring-cyan-500 border-gray-300 rounded"
              />
              <label htmlFor="allowHybridization" className="ml-2 block text-sm text-slate-900">
                Allow Hybridization
              </label>
            </div>
          </div>
        </BrandModalBody>
        <BrandModalFooter>
          <button
            type="button"
            onClick={onClose}
            className="btn btn-secondary text-sm"
          >
            Cancel
          </button>
          <button
            type="submit"
            className="btn btn-primary text-sm"
          >
            Save
          </button>
        </BrandModalFooter>
      </form>
    </BrandModal>
  );
}
